import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RESTRICTED_COUNTRIES } from '@ubnt/link-core/constants';
import { DistanceUnitsEnum } from '@ubnt/link-core/enums';
import { DEFAULT_COUNTRY, availableCountries } from '@ubnt/wifi-calculator/utils/regulatoryLimits';
import { DEFAULT_CEILING_HEIGHT } from '@ubnt/wifi-calculator/mount/constants';
import { type RootState } from 'app/store/reducer';
import { LOCAL_STORAGE_KEYS } from 'app/storageKeys';
import { getLocalStorage, setLocalStorage } from 'utils/storage';
import { SettingsState, isStoredSettingsValid } from './utils';

export const IMPERIAL_UNIT_COUNTRIES = ['US', 'GB', 'PR', 'VI', 'GU', 'MP', 'AS', 'UM', 'VG', 'BM', 'KY'];

const defaultSettings: SettingsState = {
  ceilingHeight: DEFAULT_CEILING_HEIGHT,
  country: DEFAULT_COUNTRY,
  isVisible: false,
  showDeviceName: true,
  showAnimation: true,
  unit: DistanceUnitsEnum.Metric,
};

export const storedSettings: Readonly<Partial<SettingsState>> = getLocalStorage(LOCAL_STORAGE_KEYS.SETTINGS, {});

const initialState: SettingsState = {
  ...defaultSettings,
  ...(isStoredSettingsValid(storedSettings) ? storedSettings : {}), // in case of invalid stored settings, use default settings
  isVisible: false,
};

const storeState = (state: SettingsState) => {
  const { isVisible, ...storedState } = state;
  void isVisible;
  setLocalStorage(LOCAL_STORAGE_KEYS.SETTINGS, storedState);
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    settingsCountryUpdate: (state, action: PayloadAction<{ country: string }>) => {
      state.country = action.payload.country;
      storeState(state);
    },
    settingsUserCountryUpdate: (state, action: PayloadAction<{ country: string }>) => {
      state.userCountry = action.payload.country;
      storeState(state);
    },
    settingsToggleDeviceName: (state) => {
      state.showDeviceName = !state.showDeviceName;
      storeState(state);
    },
    settingsToggleAnimation: (state) => {
      state.showAnimation = !state.showAnimation;
      storeState(state);
    },
    settingsUnitChange: (state, action: PayloadAction<ValuesOf<typeof DistanceUnitsEnum>>) => {
      state.unit = action.payload;
      storeState(state);
    },
    settingsCeilingHeightUpdate: (state, action: PayloadAction<number>) => {
      state.ceilingHeight = action.payload;
      storeState(state);
    },
    settingsToggleVisibility: (state) => {
      state.isVisible = !state.isVisible;
    },
  },
});

export const {
  settingsCountryUpdate,
  settingsUserCountryUpdate,
  settingsToggleDeviceName,
  settingsToggleAnimation,
  settingsUnitChange,
  settingsCeilingHeightUpdate,
  settingsToggleVisibility,
} = settingsSlice.actions;

export const settingsCountrySelector = (state: RootState) => state.settings.country;
export const settingsUserCountrySelector = (state: RootState) => state.settings.userCountry;
export const settingsShowDeviceNameSelector = (state: RootState) => state.settings.showDeviceName;
export const settingsShowAnimationSelector = (state: RootState) => state.settings.showAnimation;
export const settingsIsVisibleSelector = (state: RootState) => state.settings.isVisible;
export const settingsUserLocationSelectable = (state: RootState) => {
  const userCountry = settingsUserCountrySelector(state);
  return !!userCountry && availableCountries.includes(userCountry);
};
export const settingsIsCountryRestrictedSelector = (state: RootState) => {
  const userCountry = settingsUserCountrySelector(state);
  return !!userCountry && RESTRICTED_COUNTRIES.includes(userCountry);
};
export const settingsUnitSelector = (state: RootState) => state.settings.unit;
export const settingsCeilingHeightSelector = (state: RootState) => state.settings.ceilingHeight;

export default settingsSlice.reducer;
